import { Controller } from '@hotwired/stimulus';

/**
 * Posts the meter number check in the background and swaps in the form with its result below it.
 * Without JavaScript, or when the request never reaches the server, the form posts the whole page instead.
 */
export default class extends Controller {
    static values = { errorMessage: String };

    async submit(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) {
            return;
        }
        event.preventDefault();
        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.showBusy(form);

        let response;
        try {
            response = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
        } catch {
            form.submit();
            return;
        } finally {
            this.isSubmitting = false;
        }

        // An expired session ends on the login page, which needs the whole window
        if (response.redirected) {
            window.location.assign(response.url);
            return;
        }
        // The server may already have asked EDSN, so posting again would ask a second time
        if (!response.ok) {
            this.showError(form);
            return;
        }

        this.element.innerHTML = await response.text();
    }

    showBusy(form) {
        const button = form.querySelector('button[type="submit"]');
        if (button) {
            button.disabled = true;
            button.insertAdjacentHTML('afterbegin', '<span class="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" data-spinner></span>');
        }

        this.element.querySelector('.meter-number-check-result')?.classList.add('is-outdated');
    }

    showError(form) {
        const button = form.querySelector('button[type="submit"]');
        if (button) {
            button.disabled = false;
            button.querySelector('[data-spinner]')?.remove();
        }

        const alert = document.createElement('div');
        alert.className = 'meter-number-check-result mt-6 rounded-control bg-warning-soft px-4 py-3 text-sm text-warning';
        alert.setAttribute('role', 'alert');
        alert.dataset.alert = 'warning';
        alert.textContent = this.errorMessageValue;

        // Field errors from an earlier answer no longer describe what was just submitted
        form.querySelectorAll('.invalid-feedback').forEach((feedback) => feedback.remove());
        form.querySelectorAll('.is-invalid').forEach((field) => field.classList.remove('is-invalid'));
        this.element.querySelector('.meter-number-check-result')?.remove();
        form.after(alert);
    }
}
